"use client";

import { Header, Item, Root } from "@radix-ui/react-accordion";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from "react-icons/ai";
import styles from "./accordion.module.scss";

const Accordion: React.FC<{
  onChange?: (index: number | false) => void;
  defaultIndex?: number;
  items: IAccordionItem[];
}> = ({ defaultIndex = 0, items, onChange }) => {
  const [openIndex, setOpenIndex] = useState<number | false>(defaultIndex);
  const handleChange = (value: string) => setOpenIndex(+value);
  useEffect(() => onChange?.(openIndex), [openIndex]);
  return (
    <Root
      type="single"
      defaultValue={`${defaultIndex}`}
      className="grid gap-4 h-fit"
      onValueChange={handleChange}
    >
      {items.map((item, index) => (
        <AccordionItem
          key={`accordion-item-${item.title}`}
          index={index}
          openIndex={openIndex}
          setOpenIndex={setOpenIndex}
          item={item}
        />
      ))}
    </Root>
  );
};

export default Accordion;

const MotionHeader = motion(Header);

export interface IAccordionItem {
  title: string;
  content: React.ReactNode;
}

const AccordionItem: React.FC<{
  index: number;
  openIndex: number | false;
  setOpenIndex: (i: number | false) => void;
  item: IAccordionItem;
}> = ({ index, openIndex, setOpenIndex, item: { content, title } }) => {
  const isOpen = index === openIndex;
  console.log("isOpen", index, isOpen);
  return (
    <Item
      key={`accordion-item-item-${title}`}
      value={`${index}`}
      className={classNames(styles.item, { [styles.isOpen]: isOpen })}
    >
      <MotionHeader
        initial={"0"}
        className={styles.header}
        onClick={() => setOpenIndex(isOpen ? false : index)}
      >
        {openIndex === index ? (
          <AiOutlineMinusCircle size="30px" />
        ) : (
          <AiOutlinePlusCircle size="30px" />
        )}
        <div
          id={`accordion-item-${title}-label`}
          className={classNames(styles.title, { [styles.isOpen]: isOpen })}
        >
          {title} {isOpen && "open"}
        </div>
      </MotionHeader>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            role="region"
            aria-describedby={`accordion-item-${title}-label`}
            key="content"
            initial="collapsed"
            className={styles.body}
            animate="open"
            exit="collapsed"
            variants={{
              open: { height: "auto", scale: 1 },
              collapsed: { height: 0, scale: 0.99 },
            }}
          >
            <div className={styles.content}>{content}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </Item>
  );
};
